/**
 * Range Filter
 *
 * Dynamic price filter that adapts to volatility.
 * Smooths price by only allowing moves that exceed a multiplied
 * smoothed range, filtering out noise.
 *
 * Category: Trend | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeEMA } from "./sma";

export const rangeFilterIndicator: IndicatorDefinition = {
  id: "range_filter",
  name: "Range Filter",
  shortName: "RngFlt",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "period", label: "Period", type: "number", default: 50, min: 5, max: 500 },
    { key: "multiplier", label: "Multiplier", type: "number", default: 3.0, min: 0.5, max: 10, step: 0.1 },
  ],
  plots: [
    { key: "filter", label: "Range Filter", color: "#FF9800", style: "line", lineWidth: 2 },
    { key: "upper", label: "Upper Band", color: "#4CAF50", style: "line", lineWidth: 1 },
    { key: "lower", label: "Lower Band", color: "#F44336", style: "line", lineWidth: 1 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, multiplier } = params;
    const closes = candles.map(c => c.close);

    // Compute smoothed range
    const ranges: number[] = [];
    for (let i = 0; i < candles.length; i++) {
      ranges.push(candles[i].high - candles[i].low);
    }
    const smoothedRange = computeEMA(ranges, period);

    const filterData: { time: number; value: number }[] = [];
    const upperData: { time: number; value: number }[] = [];
    const lowerData: { time: number; value: number }[] = [];

    let filt = 0;
    let prevFilt = 0;
    let started = false;

    for (let i = 0; i < candles.length; i++) {
      if (smoothedRange[i] === undefined) continue;

      const rng = multiplier * smoothedRange[i];

      if (!started) {
        filt = closes[i];
        started = true;
      } else {
        prevFilt = filt;
        if (closes[i] > prevFilt) {
          filt = closes[i] - rng < prevFilt ? prevFilt : closes[i] - rng;
        } else {
          filt = closes[i] + rng > prevFilt ? prevFilt : closes[i] + rng;
        }
      }

      filterData.push({ time: candles[i].time, value: filt });
      upperData.push({ time: candles[i].time, value: filt + rng });
      lowerData.push({ time: candles[i].time, value: filt - rng });
    }

    return [
      { key: "filter", data: filterData },
      { key: "upper", data: upperData },
      { key: "lower", data: lowerData },
    ];
  },
};
